import { useState, useEffect, useRef, useCallback } from 'react';
import type { Media, MediaTransform } from '@wineryxplorer/shared';
import { getMediaUrl } from '@wineryxplorer/shared';

interface ImageLoadState {
  loaded: boolean;
  loading: boolean;
  error: boolean;
  url?: string;
  blurUrl?: string;
  errorMessage?: string;
  retryCount?: number;
}

interface CarouselError {
  type: 'image_load' | 'media_reference' | 'carousel_operation' | 'cloudinary_config';
  message: string;
  mediaId?: string;
  timestamp: Date;
  context?: Record<string, unknown>;
}

// Error logging utility
const logCarouselError = (error: CarouselError) => {
  // Log to console in development
  if (import.meta.env.DEV) {
    console.error('[HeroCarousel Error]', {
      type: error.type,
      message: error.message,
      mediaId: error.mediaId,
      timestamp: error.timestamp,
      context: error.context,
    });
  }
  
  // In production, you might want to send to an error tracking service
  // Example: Sentry, LogRocket, etc.
  // errorTrackingService.captureException(error);
};

interface UseCarouselLogicProps {
  images: Media[];
  autoAdvanceInterval: number;
  transitionDuration: number;
  venueName: string;
}

export const useCarouselLogic = ({
  images,
  autoAdvanceInterval,
  transitionDuration,
  venueName,
}: UseCarouselLogicProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isManuallyPaused, setIsManuallyPaused] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const announceRef = useRef<HTMLDivElement>(null);

  // Touch gesture state
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);
  const [isTouching, setIsTouching] = useState(false);

  // Image loading state management
  const [imageStates, setImageStates] = useState<Map<string, ImageLoadState>>(new Map());
  const [cloudName, setCloudName] = useState<string>('');
  const [viewportWidth, setViewportWidth] = useState<number>(1024);

  // Track viewport size for responsive images
  useEffect(() => {
    const updateViewportWidth = () => {
      setViewportWidth(window.innerWidth);
    };

    updateViewportWidth();
    window.addEventListener('resize', updateViewportWidth);
    return () => window.removeEventListener('resize', updateViewportWidth);
  }, []);

  // Detect reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Validate and filter out deleted Media record references
  const validateMediaReferences = useCallback((mediaList: Media[]) => {
    return mediaList.filter(media => {
      // Check for required fields that indicate a valid Media record
      if (!media.id || !media.cloudinaryPublicId || !media.cloudinaryUrl) {
        logCarouselError({
          type: 'media_reference',
          message: 'Invalid media record: missing required fields',
          mediaId: media.id || 'unknown',
          timestamp: new Date(),
          context: {
            hasId: !!media.id,
            hasPublicId: !!media.cloudinaryPublicId,
            hasUrl: !!media.cloudinaryUrl,
            media,
          },
        });
        return false;
      }

      // Check if the media record appears to be deleted or corrupted
      if (media.cloudinaryUrl.includes('deleted') || media.cloudinaryPublicId.includes('deleted')) {
        logCarouselError({
          type: 'media_reference',
          message: 'Media record appears to be deleted',
          mediaId: media.id,
          timestamp: new Date(),
          context: {
            cloudinaryUrl: media.cloudinaryUrl,
            cloudinaryPublicId: media.cloudinaryPublicId,
          },
        });
        return false;
      }

      return true;
    });
  }, []);

  // Extract cloud name from image URLs - simpler and more reliable approach
  useEffect(() => {
    if (cloudName || images.length === 0) return;

    const validImages = validateMediaReferences(images);
    if (validImages.length > 0) {
      const match = validImages[0].cloudinaryUrl.match(/\/([^/]+)\/image\/upload/);
      if (match) {
        setCloudName(match[1]);
      } else {
        logCarouselError({
          type: 'cloudinary_config',
          message: 'Failed to extract cloud name from image URLs',
          timestamp: new Date(),
          context: {
            sampleUrl: validImages[0].cloudinaryUrl,
          },
        });
      }
    }
  }, [images, cloudName, validateMediaReferences]);

  // Clear intervals on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, []);

  // Generate optimized image URLs based on viewport
  const getOptimizedImageUrl = useCallback((media: Media, isBlur = false): string => {
    if (!cloudName) return media.cloudinaryUrl;

    const transform: MediaTransform = {
      width: Math.min(viewportWidth * 2, 1920), // 2x for retina, max 1920px
      height: 512, // Fixed height for hero images
      crop: 'fill',
      format: 'auto',
      quality: isBlur ? 10 : 80,
    };

    if (isBlur) {
      transform.width = 40; // Very small for blur placeholder
      transform.height = 26;
    }

    return getMediaUrl(media.cloudinaryPublicId, cloudName, 'cloudinary', transform);
  }, [cloudName, viewportWidth]);

  // Preload image with enhanced error handling and retry logic
  const preloadImage = useCallback((media: Media, priority = false) => {
    const imageId = media.id;
    const currentState = imageStates.get(imageId);

    // Skip if already loaded or loading
    if (currentState?.loaded || currentState?.loading) {
      return Promise.resolve();
    }

    // Check if we've exceeded retry attempts
    const retryCount = currentState?.retryCount ?? 0;
    const maxRetries = 2;
    
    if (currentState?.error && retryCount >= maxRetries) {
      logCarouselError({
        type: 'image_load',
        message: `Image failed to load after ${maxRetries} retries`,
        mediaId: media.id,
        timestamp: new Date(),
        context: {
          cloudinaryUrl: media.cloudinaryUrl,
          cloudinaryPublicId: media.cloudinaryPublicId,
          retryCount,
        },
      });
      return Promise.resolve();
    }

    // Set loading state
    setImageStates(prev => new Map(prev.set(imageId, {
      loaded: false,
      loading: true,
      error: false,
      url: getOptimizedImageUrl(media),
      blurUrl: getOptimizedImageUrl(media, true),
      retryCount,
    })));

    return new Promise<void>((resolve) => {
      const img = new Image();
      
      img.onload = () => {
        setImageStates(prev => new Map(prev.set(imageId, {
          loaded: true,
          loading: false,
          error: false,
          url: getOptimizedImageUrl(media),
          blurUrl: getOptimizedImageUrl(media, true),
          retryCount: 0, // Reset retry count on success
        })));
        resolve();
      };

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      img.onerror = (event) => {
        const errorMessage = `Failed to load image: ${media.cloudinaryUrl}`;
        
        logCarouselError({
          type: 'image_load',
          message: errorMessage,
          mediaId: media.id,
          timestamp: new Date(),
          context: {
            cloudinaryUrl: media.cloudinaryUrl,
            cloudinaryPublicId: media.cloudinaryPublicId,
            retryCount: retryCount + 1,
            priority,
            event: "loaderror",
          },
        });

        setImageStates(prev => new Map(prev.set(imageId, {
          loaded: false,
          loading: false,
          error: true,
          url: getOptimizedImageUrl(media),
          blurUrl: getOptimizedImageUrl(media, true),
          errorMessage,
          retryCount: retryCount + 1,
        })));
        
        resolve(); // Resolve anyway to continue carousel
      };

      // Set timeout for image loading
      const loadTimeout = setTimeout(() => {
        img.onload = null;
        img.onerror = null;
        
        const timeoutMessage = `Image load timeout: ${media.cloudinaryUrl}`;
        
        logCarouselError({
          type: 'image_load',
          message: timeoutMessage,
          mediaId: media.id,
          timestamp: new Date(),
          context: {
            cloudinaryUrl: media.cloudinaryUrl,
            cloudinaryPublicId: media.cloudinaryPublicId,
            timeout: true,
            retryCount: retryCount + 1,
          },
        });

        setImageStates(prev => new Map(prev.set(imageId, {
          loaded: false,
          loading: false,
          error: true,
          url: getOptimizedImageUrl(media),
          blurUrl: getOptimizedImageUrl(media, true),
          errorMessage: timeoutMessage,
          retryCount: retryCount + 1,
        })));
        
        resolve();
      }, priority ? 5000 : 10000); // Shorter timeout for priority images

      img.src = getOptimizedImageUrl(media);
      
      // Clear timeout if image loads successfully
      const originalOnLoad = img.onload;
      img.onload = (event) => {
        clearTimeout(loadTimeout);
        originalOnLoad.call(img, event);
      };
      
      const originalOnError = img.onerror;
      img.onerror = (event) => {
        clearTimeout(loadTimeout);
        originalOnError.call(img, event);
      };
    });
  }, [imageStates, getOptimizedImageUrl]);

  // Initialize image loading - preload first image immediately, lazy load others
  useEffect(() => {
    if (images.length === 0 || !cloudName) return;

    // Validate media references first
    const validImages = validateMediaReferences(images);
    
    if (validImages.length === 0) {
      logCarouselError({
        type: 'media_reference',
        message: 'No valid media references found after validation',
        timestamp: new Date(),
        context: {
          originalCount: images.length,
          validCount: 0,
        },
      });
      return;
    }

    // Preload first valid image immediately
    if (validImages.length > 0) {
      preloadImage(validImages[0], true);
    }

    // Lazy load subsequent images in background
    const loadRemainingImages = async () => {
      for (let i = 1; i < validImages.length; i++) {
        await new Promise(resolve => setTimeout(resolve, 100)); // Small delay between loads
        preloadImage(validImages[i]);
      }
    };

    // Start lazy loading after a short delay
    const timeoutId = setTimeout(loadRemainingImages, 500);
    return () => clearTimeout(timeoutId);
  }, [images, cloudName, preloadImage, validateMediaReferences]);

  // Get available (loaded or loading) images, filtering out errored ones
  const getAvailableImages = useCallback(() => {
    return images.filter(image => {
      const state = imageStates.get(image.id);
      return !state?.error; // Include images that haven't loaded yet or are loaded
    });
  }, [images, imageStates]);

  // Auto-skip to next available image if current image fails to load
  useEffect(() => {
    if (images.length <= 1) return;

    const currentImage = images[currentIndex];
    const currentState = imageStates.get(currentImage.id);

    if (currentState?.error) {
      const availableImages = getAvailableImages();
      if (availableImages.length === 0) {
        // All images failed - this will be handled by the render logic
        return;
      }

      // Find next available image
      let nextIndex = (currentIndex + 1) % images.length;
      let attempts = 0;
      
      while (attempts < images.length) {
        const nextImage = images[nextIndex];
        const nextState = imageStates.get(nextImage.id);
        
        if (!nextState?.error) {
          setCurrentIndex(nextIndex);
          break;
        }
        
        nextIndex = (nextIndex + 1) % images.length;
        attempts++;
      }
    }
  }, [currentIndex, images, imageStates, getAvailableImages]);

  // Auto-advance functionality
  const startAutoAdvance = useCallback(() => {
    if (images.length <= 1 || isPaused || isManuallyPaused || prefersReducedMotion) return;

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, autoAdvanceInterval);
  }, [images.length, isPaused, isManuallyPaused, prefersReducedMotion, autoAdvanceInterval]);

  const stopAutoAdvance = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Start/stop auto-advance based on pause state
  useEffect(() => {
    if (isPaused || isManuallyPaused || prefersReducedMotion) {
      stopAutoAdvance();
    } else {
      startAutoAdvance();
    }

    return stopAutoAdvance;
  }, [isPaused, isManuallyPaused, prefersReducedMotion, startAutoAdvance, stopAutoAdvance]);

  // Handle transition state management
  useEffect(() => {
    if (images.length <= 1) return;

    setIsTransitioning(true);
    
    // Clear any existing transition timeout
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }

    // Use instant transitions for reduced motion preference
    const effectiveTransitionDuration = prefersReducedMotion ? 0 : transitionDuration;

    // Set transition to false after transition duration
    transitionTimeoutRef.current = setTimeout(() => {
      setIsTransitioning(false);
    }, effectiveTransitionDuration);

    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
    };
  }, [currentIndex, transitionDuration, prefersReducedMotion, images.length]);

  // Announce image changes to screen readers
  useEffect(() => {
    if (images.length <= 1 || !announceRef.current) return;

    const currentImage = images[currentIndex];
    const imageState = imageStates.get(currentImage.id);
    
    if (!imageState?.error) {
      announceRef.current.textContent = `Showing image ${currentIndex + 1} of ${images.length}: ${venueName}`;
    }
  }, [currentIndex, images, venueName, imageStates]);

  // Navigation handlers with enhanced error recovery
  const goToNext = useCallback(() => {
    try {
      if (isTransitioning || images.length <= 1) return;
      
      const availableImages = getAvailableImages();
      if (availableImages.length <= 1) return;

      let nextIndex = (currentIndex + 1) % images.length;
      let attempts = 0;
      
      // Skip failed images
      while (attempts < images.length) {
        const nextImage = images[nextIndex];
        const nextState = imageStates.get(nextImage.id);
        
        if (!nextState?.error) {
          setCurrentIndex(nextIndex);
          break;
        }
        
        nextIndex = (nextIndex + 1) % images.length;
        attempts++;
      }

      // If all images are in error state, try to recover
      if (attempts >= images.length) {
        logCarouselError({
          type: 'carousel_operation',
          message: 'All images in error state during navigation, attempting recovery',
          timestamp: new Date(),
          context: {
            currentIndex,
            totalImages: images.length,
            availableImages: availableImages.length,
          },
        });
        
        // Reset error states and try to reload images
        setImageStates(new Map());
        setCurrentIndex(0);
      }
    } catch (error) {
      logCarouselError({
        type: 'carousel_operation',
        message: 'Error in goToNext navigation',
        timestamp: new Date(),
        context: {
          error: error instanceof Error ? error.message : 'Unknown error',
          currentIndex,
          imagesLength: images.length,
        },
      });
    }
  }, [isTransitioning, images, currentIndex, getAvailableImages, imageStates]);

  const goToPrevious = useCallback(() => {
    try {
      if (isTransitioning || images.length <= 1) return;
      
      const availableImages = getAvailableImages();
      if (availableImages.length <= 1) return;

      let prevIndex = (currentIndex - 1 + images.length) % images.length;
      let attempts = 0;
      
      // Skip failed images
      while (attempts < images.length) {
        const prevImage = images[prevIndex];
        const prevState = imageStates.get(prevImage.id);
        
        if (!prevState?.error) {
          setCurrentIndex(prevIndex);
          break;
        }
        
        prevIndex = (prevIndex - 1 + images.length) % images.length;
        attempts++;
      }

      // If all images are in error state, try to recover
      if (attempts >= images.length) {
        logCarouselError({
          type: 'carousel_operation',
          message: 'All images in error state during previous navigation, attempting recovery',
          timestamp: new Date(),
          context: {
            currentIndex,
            totalImages: images.length,
            availableImages: availableImages.length,
          },
        });
        
        // Reset error states and try to reload images
        setImageStates(new Map());
        setCurrentIndex(0);
      }
    } catch (error) {
      logCarouselError({
        type: 'carousel_operation',
        message: 'Error in goToPrevious navigation',
        timestamp: new Date(),
        context: {
          error: error instanceof Error ? error.message : 'Unknown error',
          currentIndex,
          imagesLength: images.length,
        },
      });
    }
  }, [isTransitioning, images, currentIndex, getAvailableImages, imageStates]);

  const goToSlide = useCallback((index: number) => {
    try {
      if (isTransitioning || images.length <= 1 || index === currentIndex) return;
      
      // Validate index bounds
      if (index < 0 || index >= images.length) {
        logCarouselError({
          type: 'carousel_operation',
          message: 'Invalid slide index in goToSlide',
          timestamp: new Date(),
          context: {
            requestedIndex: index,
            currentIndex,
            totalImages: images.length,
          },
        });
        return;
      }
      
      const targetImage = images[index];
      const targetState = imageStates.get(targetImage.id);
      
      // Don't navigate to failed images, but try to retry loading
      if (targetState?.error) {
        logCarouselError({
          type: 'carousel_operation',
          message: 'Attempted navigation to failed image, retrying load',
          mediaId: targetImage.id,
          timestamp: new Date(),
          context: {
            targetIndex: index,
            currentIndex,
            errorMessage: targetState.errorMessage,
          },
        });
        
        // Attempt to retry loading the image
        preloadImage(targetImage, true);
        return;
      }
      
      setCurrentIndex(index);
    } catch (error) {
      logCarouselError({
        type: 'carousel_operation',
        message: 'Error in goToSlide navigation',
        timestamp: new Date(),
        context: {
          error: error instanceof Error ? error.message : 'Unknown error',
          targetIndex: index,
          currentIndex,
          imagesLength: images.length,
        },
      });
    }
  }, [isTransitioning, images, currentIndex, imageStates, preloadImage]);

  // Touch gesture handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (images.length <= 1) return;
    
    const touch = e.touches[0];
    setTouchStart({ x: touch.clientX, y: touch.clientY });
    setTouchEnd(null);
    setIsTouching(true);
    
    // Pause auto-advance during touch interaction
    setIsPaused(true);
  }, [images.length]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStart || images.length <= 1) return;
    
    const touch = e.touches[0];
    setTouchEnd({ x: touch.clientX, y: touch.clientY });
  }, [touchStart, images.length]);

  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd || images.length <= 1) {
      setIsTouching(false);
      setIsPaused(false);
      return;
    }

    const deltaX = touchStart.x - touchEnd.x;
    const deltaY = Math.abs(touchStart.y - touchEnd.y);
    
    // Minimum swipe distance (50px) and ensure horizontal swipe
    const minSwipeDistance = 50;
    const maxVerticalDistance = 100;
    
    if (Math.abs(deltaX) > minSwipeDistance && deltaY < maxVerticalDistance) {
      // Prevent default to avoid scrolling during swipe
      if (deltaX > 0) {
        // Swiped left - go to next image
        goToNext();
      } else {
        // Swiped right - go to previous image  
        goToPrevious();
      }
    }

    // Reset touch state and resume auto-advance after a short delay
    setTouchStart(null);
    setTouchEnd(null);
    setIsTouching(false);
    
    // Resume auto-advance after touch interaction ends
    setTimeout(() => {
      setIsPaused(false);
    }, 500); // Small delay to prevent immediate auto-advance
  }, [touchStart, touchEnd, images.length, goToNext, goToPrevious]);

  // Hover handlers for pause behavior (desktop)
  const handleMouseEnter = () => {
    if (!isTouching) {
      setIsPaused(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isTouching) {
      setIsPaused(false);
    }
  };

  // Keyboard navigation handler
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (images.length <= 1) return;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        goToPrevious();
        break;
      case 'ArrowRight':
        e.preventDefault();
        goToNext();
        break;
      case ' ':
      case 'Spacebar':
        e.preventDefault();
        setIsManuallyPaused(prev => !prev);
        break;
      case 'Home':
        e.preventDefault();
        goToSlide(0);
        break;
      case 'End':
        e.preventDefault();
        goToSlide(images.length - 1);
        break;
    }
  }, [images.length, goToPrevious, goToNext, goToSlide]);

  // Toggle manual pause
  const toggleManualPause = useCallback(() => {
    setIsManuallyPaused(prev => !prev);
  }, []);

  const availableImages = getAvailableImages();

  return {
    // State
    currentIndex,
    isTransitioning,
    isPaused,
    isManuallyPaused,
    prefersReducedMotion,
    isTouching,
    imageStates,
    cloudName,
    availableImages,
    
    // Refs
    carouselRef,
    announceRef,
    
    // Handlers
    goToNext,
    goToPrevious,
    goToSlide,
    toggleManualPause,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleMouseEnter,
    handleMouseLeave,
    handleKeyDown,
  };
};